// components/StarredContent.tsx
import React, { useState, useMemo } from "react";
import { withAuth } from "../api/auth.ts";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Stack,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Folder as FolderIcon,
  BookmarkTwoTone as BookmarkIcon,
  ArrowUpward as AscIcon,
  ArrowDownward as DescIcon,
} from "@mui/icons-material";
import { formatFileSize, formatDate } from "../utils/formatUtils.ts";
import { usePaletteColors } from "../hooks/useDarkMode.ts";
import { PaletteColor } from "../types/PaletteColor.ts";
import { LoadingOverlay } from "./loadingOverlay.tsx";
import { FileThumbnail } from "./FileThumbnail.tsx";
import { FileTypeIcon } from "./FileTypeIcon.tsx";

export interface BookmarkedItemDto {
  id: string;
  type: "folder" | "file";
  name: string;
  size: number;
  updatedAt: string;
  hpcode: number;
  roomId: number;
  thumbnailStatus: number; // 0:未生成, 1:生成済, 2:非対応・生成失敗 (フォルダは常に0)
}

interface BookmarkedContentProps {
  items: BookmarkedItemDto[];
  isLoading: boolean;
  honbuId: number;
  palette: PaletteColor;
  isMobile?: boolean;
  onRemoveBookmark: (id: string, type: "folder" | "file") => void;
  onItemClick: (item: BookmarkedItemDto) => void;
}

type SortKey = "name" | "roomId" | "updatedAt" | "size";
type SortOrder = "asc" | "desc";

const sortKeyLabels: Record<SortKey, string> = {
  name: "名前",
  roomId: "場所",
  updatedAt: "更新日",
  size: "サイズ",
};

export const BookmarkedContent: React.FC<BookmarkedContentProps> = ({
  items,
  isLoading,
  honbuId,
  palette,
  isMobile = false,
  onRemoveBookmark,
  onItemClick,
}) => {
  const { accentColor, isDarkMode } = usePaletteColors(palette);

  // サムネイル取得 URL (ファイル、かつ thumbnailStatus=2 [非対応] でない場合のみ)
  const getThumbnailUrl = (item: BookmarkedItemDto): string | undefined =>
    item.type === "file" && item.thumbnailStatus !== 2
      ? withAuth(`/api/files/thumbnail/${item.id}?honbuId=${honbuId}&roomId=${item.roomId}`)
      : undefined;
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "name") {
        cmp = a.name.localeCompare(b.name, "ja");
      } else if (sortKey === "roomId") {
        cmp = a.roomId - b.roomId;
      } else if (sortKey === "updatedAt") {
        cmp = a.updatedAt.localeCompare(b.updatedAt);
      } else if (sortKey === "size") {
        cmp = a.size - b.size;
      }
      return sortOrder === "asc" ? cmp : -cmp;
    });
  }, [items, sortKey, sortOrder]);

  if (isLoading) return <LoadingOverlay message="Now Loading..." color={palette} />;

  return (
    <Box sx={{ p: isMobile ? 2 : 4, height: "100%", overflowY: "auto" }}>
      <Box
        sx={{
          pb: 1.5,
          mb: 2,
          borderBottom: "2px dashed",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <BookmarkIcon sx={{ color: accentColor, fontSize: 24 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: isDarkMode ? "grey.400" : "grey.800",
            }}
          >
            ブックマーク
          </Typography>
        </Stack>
      </Box>

      {items.length === 0 ? (
        <Typography color="text.secondary">
          ブックマークした項目はありません。
        </Typography>
      ) : isMobile ? (
        <>
          {/* モバイル用ソートコントロール */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 0 }}>
              <Select
                value={sortKey}
                onChange={(e) => handleSort(e.target.value as SortKey)}
                variant="standard"
                disableUnderline
                sx={{
                  fontSize: "0.8rem",
                  color: "text.secondary",
                  "& .MuiSelect-select": { py: 0, pr: "20px !important" },
                }}
              >
                {(Object.keys(sortKeyLabels) as SortKey[]).map((key) => (
                  <MenuItem key={key} value={key} dense>
                    {sortKeyLabels[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => setSortOrder((d) => (d === "asc" ? "desc" : "asc"))}
              sx={{ color: "text.secondary" }}
            >
              {sortOrder === "asc" ? (
                <AscIcon sx={{ fontSize: 18 }} />
              ) : (
                <DescIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Stack>

          {/* モバイル用リスト */}
          <Box>
            {sortedItems.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  cursor: item.type === "file" ? "pointer" : "default",
                }}
                onClick={() => item.type === "file" && onItemClick(item)}
              >
                <Box sx={{ flexShrink: 0, mr: 1.5, display: "flex", alignItems: "center" }}>
                  {getThumbnailUrl(item) ? (
                    <FileThumbnail
                      src={getThumbnailUrl(item)!}
                      size={24}
                      fallback={
                        <Box sx={{ color: "text.secondary", display: "flex" }}>
                          <FileTypeIcon fileName={item.name} size={20} />
                        </Box>
                      }
                    />
                  ) : item.type === "folder" ? (
                    <FolderIcon fontSize="small" sx={{ color: "#FFB020" }} />
                  ) : (
                    <Box sx={{ color: "text.secondary", display: "flex" }}>
                      <FileTypeIcon fileName={item.name} size={20} />
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap fontWeight={500}>
                    {item.name}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      Room: {item.roomId}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">·</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(item.updatedAt)}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">·</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.type === "file" ? formatFileSize(item.size) : "-"}
                    </Typography>
                  </Stack>
                </Box>
                <Tooltip title="ブックマークを外す">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveBookmark(item.id, item.type);
                    }}
                    sx={{ flexShrink: 0 }}
                  >
                    <BookmarkIcon sx={{ color: "#FFB020" }} fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead sx={{ bgcolor: "action.hover" }}>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === "name"}
                    direction={sortKey === "name" ? sortOrder : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    名前
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === "roomId"}
                    direction={sortKey === "roomId" ? sortOrder : "asc"}
                    onClick={() => handleSort("roomId")}
                  >
                    場所 (RoomID)
                  </TableSortLabel>
                </TableCell>
                <TableCell width={160}>
                  <TableSortLabel
                    active={sortKey === "updatedAt"}
                    direction={sortKey === "updatedAt" ? sortOrder : "asc"}
                    onClick={() => handleSort("updatedAt")}
                  >
                    更新日
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" width={80} sx={{ whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sortKey === "size"}
                    direction={sortKey === "size" ? sortOrder : "asc"}
                    onClick={() => handleSort("size")}
                  >
                    サイズ
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">解除</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ cursor: item.type === "file" ? "pointer" : "default" }}
                  onClick={() => item.type === "file" && onItemClick(item)}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {getThumbnailUrl(item) ? (
                        <FileThumbnail
                          src={getThumbnailUrl(item)!}
                          size={28}
                          fallback={
                            <Box
                              sx={{ color: "text.secondary", display: "flex" }}
                            >
                              <FileTypeIcon fileName={item.name} size={24} />
                            </Box>
                          }
                        />
                      ) : item.type === "folder" ? (
                        <FolderIcon sx={{ color: "#FFB020" }} />
                      ) : (
                        <Box sx={{ color: "text.secondary", display: "flex" }}>
                          <FileTypeIcon fileName={item.name} size={24} />
                        </Box>
                      )}
                      <Typography variant="body2" fontWeight={500}>
                        {item.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      Room: {item.roomId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(item.updatedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption">
                      {item.type === "file" ? formatFileSize(item.size) : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="ブックマークを外す">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveBookmark(item.id, item.type);
                        }}
                      >
                        <BookmarkIcon sx={{ color: "#FFB020" }} fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
